import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import type {TRootState} from '..'
import {extractUserFromToken} from '../../utils/jwt-utils'
import type {
  ResetRequest,
  RequestSignup,
  SignoutResponse,
  MSPAuthResponse,
  MSPSigninRequest,
  MSPPasswordResetRequest,
  MSPTemporaryPasswordResetRequest,
} from '../../models'

// Cognito AuthenticationResultType response shape
interface CognitoAuthResponse {
  IdToken?: string
  AccessToken?: string
  ExpiresIn?: number
  RefreshToken?: string
  TokenType?: string
}

// IOP token exchange request / response shapes
export interface IopLoginRequest {
  code: string
  redirectUri: string
  codeVerifier?: string
}

export interface IopLoginResponse {
  accessToken: string
  idToken: string
  refreshToken: string
  expiresIn: number
  tokenType: string
  email: string
  userId: string
  iopHumanGuid?: string
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_BACKEND_URL,
    prepareHeaders: (headers: Headers, {getState}: {getState: () => unknown}): Headers => {
      const token = (getState() as TRootState).root.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  endpoints: (builder) => ({
    signin: builder.mutation<MSPAuthResponse, MSPSigninRequest>({
      query: ({emailAddress, password}) => ({
        url: '/user/signIn',
        method: 'POST',
        body: {
          emailAddress,
          password,
        },
      }),
      transformResponse: (response: CognitoAuthResponse): MSPAuthResponse => {
        // The MSP endpoint returns the Cognito AuthenticationResultType
        // Extract user info from JWT token
        const userInfo = extractUserFromToken(response.IdToken || '')

        return {
          idToken: response.IdToken || '',
          accessToken: response.AccessToken || '',
          expiresIn: response.ExpiresIn || 3600,
          refreshToken: response.RefreshToken || '',
          TokenType: response.TokenType || 'Bearer',
          emailAddress: userInfo?.emailAddress || '',
          userId: userInfo?.userId || '',
          isSuccess: true,
          message: 'Sign in successful',
        }
      },
    }),

    signout: builder.mutation<SignoutResponse, {email: string}>({
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        const isIopSession = window.localStorage.getItem('isIopSession') === 'true'

        try {
          await baseQuery({
            url: '/api/zoom/oauth/disconnect',
            method: 'POST',
          })
        } catch {
          // Best effort — Zoom may not be connected
        }
        if (!isIopSession) {
          try {
            await baseQuery({
              url: '/user/signOut',
              method: 'POST',
            })
          } catch {
            // Best effort — still sign out locally if backend call fails
          }
        }
        window.localStorage.clear()
        window.sessionStorage.clear()
        return {data: {message: 'You have been signed out'}}
      },
    }),

    // Password reset endpoints using MSP platform
    initiatePasswordReset: builder.mutation<null, {email: string}>({
      query: ({email}) => ({
        url: `/user/initiatePasswordReset/${email}`,
        method: 'PUT',
      }),
    }),

    confirmPasswordReset: builder.mutation<null, MSPPasswordResetRequest>({
      query: (body) => ({
        url: '/user/confirmPasswordReset',
        method: 'PUT',
        body,
      }),
    }),

    temporaryPasswordReset: builder.mutation<null, MSPTemporaryPasswordResetRequest>({
      query: (body) => ({
        url: '/user/temporaryPasswordReset',
        method: 'PUT',
        body,
      }),
    }),

    // Change Password endpoints
    verifyUserPassword: builder.mutation<null, {password: string}>({
      query: ({password}) => ({
        url: '/user/verifyUserPassword',
        method: 'POST',
        body: {
          password: decodeURIComponent(password),
        },
      }),
    }),

    updateUserPassword: builder.mutation<null, {password: string}>({
      query: ({password}) => ({
        url: '/user/updateUserPassword',
        method: 'PUT',
        body: {
          password: decodeURIComponent(password),
        },
        params: {
          application: 'MSP',
        },
      }),
    }),

    /**
     * Refresh session using refresh token
     * Returns new ID token and access token
     * Note: Backend endpoint needed - uses Cognito REFRESH_TOKEN_AUTH flow
     */
    refreshSession: builder.mutation<MSPAuthResponse, {refreshToken: string}>({
      query: ({refreshToken}) => ({
        url: '/user/refreshSession',
        method: 'POST',
        body: {
          refreshToken,
        },
      }),
      transformResponse: (response: CognitoAuthResponse): MSPAuthResponse => {
        // The refresh endpoint should return new tokens
        const userInfo = extractUserFromToken(response.IdToken || '')

        return {
          idToken: response.IdToken || '',
          accessToken: response.AccessToken || '',
          expiresIn: response.ExpiresIn || 3600,
          refreshToken: response.RefreshToken || '', // May not be returned on refresh
          TokenType: response.TokenType || 'Bearer',
          emailAddress: userInfo?.emailAddress || '',
          userId: userInfo?.userId || '',
          isSuccess: true,
          message: 'Session refreshed successfully',
        }
      },
    }),

    iopLogin: builder.mutation<MSPAuthResponse, IopLoginRequest>({
      query: (body) => ({
        url: '/user/iop-login',
        method: 'POST',
        body,
      }),
      transformResponse: (response: IopLoginResponse): MSPAuthResponse => {
        return {
          idToken: response.idToken,
          accessToken: response.accessToken,
          expiresIn: response.expiresIn,
          refreshToken: response.refreshToken,
          TokenType: response.tokenType,
          emailAddress: response.email,
          userId: response.userId,
          isSuccess: true,
          message: 'IOP sign in successful',
        }
      },
    }),

    // Legacy endpoints - keeping for backward compatibility during migration
    register: builder.mutation<null, RequestSignup>({
      queryFn: () => {
        // TODO: Implement MSP registration endpoint when available
        return {error: {data: 'Registration not implemented yet', status: 501}}
      },
    }),

    reset: builder.mutation<null, Partial<ResetRequest>>({
      queryFn: () => {
        // TODO: Remove after migration complete - use temporaryPasswordReset instead
        return {error: {data: 'Use temporaryPasswordReset instead', status: 501}}
      },
    }),
  }),
})

export const {
  useSigninMutation,
  useIopLoginMutation,
  useSignoutMutation,
  useInitiatePasswordResetMutation,
  useConfirmPasswordResetMutation,
  useTemporaryPasswordResetMutation,
  useVerifyUserPasswordMutation,
  useUpdateUserPasswordMutation,
  useRefreshSessionMutation,
  // Legacy exports
  useResetMutation,
  useRegisterMutation,
} = authApi
